import {GetValues, GetValues_ForSchema} from "js-vextensions";
import {AddSchema} from "mobx-graphlink";
import {nativeBridge} from "../../../Utils/Bridge/Bridge_Native.js";
import {AssertUnreachable} from "web-vcore";

export enum EffectPointerType {
	soundTag = "soundTag",
	soundFile = "soundFile",
	tts = "tts",
	shakeTag = "shakeTag",
	lightTag = "lightTag",
	scriptTag = "scriptTag",
}
AddSchema("EffectPointerType", {oneOf: GetValues_ForSchema(EffectPointerType)});

export enum SoundPanningType {
	none = "none",
	simple = "simple",
	simple_slow = "simple_slow",
	spatial = "spatial",
}
AddSchema("SoundPanningType", {enum: GetValues(SoundPanningType)});
export function GetSoundPanningTypeDescriptions(includeFallback: boolean) {
	return [
		includeFallback && `Fallback: Use whatever is set as the "Global panning-type fallback" under General.`,
		`None: No panning is applied.`,
		`Simple: Simple panning (100% left <> 100% right) based on global panning source.`,
		`Simple_Slow: Same as Simple, except every second direction-switch trigger gets ignored.`,
		`Spatial: Evolving HRTF panning based on provided settings.`,
	].filter(a=>a).join("\n");
}

export enum SoundMutingType {
	none = "none",
	simple = "simple",
	simple_slow = "simple_slow",
}
AddSchema("SoundMutingType", {enum: GetValues(SoundMutingType)});
export function GetSoundMutingTypeDescriptions(includeFallback: boolean) {
	return [
		includeFallback && `Fallback: Use whatever is set as the "Global muting-type fallback" under General.`,
		`None: No muting is applied.`,
		`Simple: Simple muting (100% muted <> 100% audible) based on global muting source.`,
		`Simple_Slow: Same as Simple, except every second muting-switch trigger gets ignored.`,
	].filter(a=>a).join("\n");
}

export enum CycleCountType {
	none = "none",
	count_up = "count_up",
	count_down = "count_down",
	count_down_solveTimeLeft = "count_down_solveTimeLeft",
}
AddSchema("CycleCountType", {enum: GetValues(CycleCountType)});

export function ResetEffectPointerBasedOnType(effect: EffectPointer) {
	const newData = NewEffectPointerBasedOnType(effect.type);
	// clear existing data
	for (const key in newData) {
		delete effect[key];
	}
	// set new data
	Object.assign(effect, newData);
}
export function NewEffectPointerBasedOnType(type: EffectPointerType) {
	if (type == EffectPointerType.soundTag) return new EffectPointer({type, soundEffectTag: ""});
	if (type == EffectPointerType.soundFile) return new EffectPointer({type, soundFile: {filePath: "", volume: 1, durationLimit: -1, loop: true, runCount: 1, wordsIgnoreGroup: 0}});
	if (type == EffectPointerType.tts) return new EffectPointer({type, tts: {soundTag: "", text: ""}});
	if (type == EffectPointerType.shakeTag) return new EffectPointer({type, shakeEffectTag: ""});
	if (type == EffectPointerType.lightTag) return new EffectPointer({type, lightEffectTag: ""});
	if (type == EffectPointerType.scriptTag)  return new EffectPointer({type, scriptEffectTag: ""});
	AssertUnreachable(type);
}

export class RunConfig {
	subpathInclude?: string|n;
	subpathExclude?: string|n;
	volumeMultiplier?: number|n;
	delay_post?: number|n; // in secondss
}
AddSchema("RunConfig", {
	properties: {
		subpathInclude: {type: ["null", "string"]},
		subpathExclude: {type: ["null", "string"]},
		volumeMultiplier: {type: ["null", "number"]},
		delay_post: {type: ["null", "number"]},
	},
});

export class EffectPointer {
	static GetEffectTag(effect: EffectPointer) {
		if (effect.soundEffectTag) return effect.soundEffectTag;
		if (effect.tts) return effect.tts.soundTag;
		if (effect.shakeEffectTag) return effect.shakeEffectTag;
		if (effect.lightEffectTag) return effect.lightEffectTag;
		if (effect.scriptEffectTag) return effect.scriptEffectTag;
		return null;
	}

	constructor(data?: Partial<EffectPointer>) {
		Object.assign(this, data);
	}

	type = EffectPointerType.soundTag;
	recordSessionEvent: boolean|n;
	afterEffect: EffectPointer|n;
	afterEffect_delay: number|n; // in seconds; -1 means start immediately (before the current effect completes)

	// sound
	soundEffectTag: string|n;
	soundFile: {
		filePath: string;
		volume?: number|n;
		volume_evolve?: boolean|n;
		volume_evolveInterval?: number|n; // in seconds
		volume_evolveValues?: number[]|n;
		volume_evolveAcrossRuns?: boolean|n;
		speed_multiplier?: {min: number, max: number}|n;
		speed_duration?: number|n;
		startTime?: {min: number, max: number}|n; // in seconds
		durationLimit?: number|n; // in seconds
		loop?: boolean|n;
		wordsIgnoreGroup?: number|n;

		// panning-mode settings
		panningType?: SoundPanningType|n;
		spatial_initialDirection_clockwise?: boolean|n;
		spatial_switchDirInterval_min?: number|n; // in seconds
		spatial_switchDirInterval_max?: number|n; // in seconds
		spatial_distance?: number|n;
		spatial_revolutionDuration?: number|n; // in seconds
		spatial_cycleComplete_countType?: CycleCountType|n;
		spatial_cycleComplete_countStart?: number|n;
		spatial_cycleComplete_countTTSInSolvingPhaseOnly?: boolean|n;
		spatial_cycleComplete_countTTSVolume?: number|n;

		// muting-mode settings
		mutingType?: SoundMutingType|n;

		runCount?: number|n;
		runConfigs_countOverride?: number|n;
		runConfigs?: RunConfig[];
	}|n;
	tts: {
		soundTag?: string|n;
		volume?: number|n;
		text: string;
	}|n;

	// shake
	shakeEffectTag: string|n;

	// light
	lightEffectTag: string|n;

	// script
	scriptEffectTag: string|n;
}
AddSchema("EffectPointer", {
	properties: {
		type: {type: "string", enum: Object.values(EffectPointerType)},
		afterEffect: {type: ["null", "object"]},
		afterEffect_delay: {type: ["null", "number"]},

		soundEffectTag: {type: ["null", "string"]},
		soundFile: {
			type: ["null", "object"],
			properties: {
				filePath: {type: "string"},
				volume: {type: ["null", "number"]},
				volume_evolve: {type: ["null", "boolean"]},
				volume_evolveInterval: {type: ["null", "number"]},
				volume_evolveValues: {type: ["null", "array"], items: {type: "number"}},
				volume_evolveAcrossRuns: {type: ["null", "boolean"]},
				durationLimit: {type: ["null", "number"]},
				speed_multiplier: {type: ["null", "number"]},
				speed_multiplierRange: {
					type: ["null", "object"],
					properties: {
						min: {type: "number"},
						max: {type: "number"},
					},
				},
				speed_duration: {type: ["null", "number"]},
				loop: {type: ["null", "boolean"]},
				wordsIgnoreGroup: {type: ["null", "number"]},

				panningType: {$ref: "SoundPanningType"},
				spatial_initialDirection_clockwise: {type: ["null", "boolean"]},
				spatial_switchDirInterval_min: {type: ["null", "number"]},
				spatial_switchDirInterval_max: {type: ["null", "number"]},
				spatial_distance: {type: ["null", "number"]},
				spatial_revolutionDuration: {type: ["null", "number"]},
				spatial_cycleComplete_countType: {type: ["null", "string"]},
				spatial_cycleComplete_countStart: {type: ["null", "number"]},
				spatial_cycleComplete_countTTSInSolvingPhaseOnly: {type: ["null", "boolean"]},
				spatial_cycleComplete_countTTSVolume: {type: ["null", "number"]},

				mutingType: {$ref: "SoundMutingType"},

				runCount: {type: ["null", "number"]},
				runConfigs_countOverride: {type: ["null", "number"]},
				runConfigs: {items: {$ref: "RunConfig"}},
			},
		},
		tts: {
			type: ["null", "object"],
			properties: {
				soundTag: {type: ["null", "string"]},
				text: {type: "string"},
			},
		},
		shakeEffectTag: {type: ["null", "string"]},
		lightEffectTag: {type: ["null", "string"]},
		scriptEffectTag: {type: ["null", "string"]},
	},
});

export function EffectPointer_UpdateRunConfigsCount(soundFile: EffectPointer["soundFile"]) {
	if (!soundFile) return;
	soundFile.runConfigs ??= [];

	const newCount = soundFile.runConfigs_countOverride ?? soundFile.runCount ?? soundFile.runConfigs.length;
	soundFile.runConfigs.length = newCount;
	for (let i = 0; i < newCount; i++) {
		soundFile.runConfigs[i] ??= {} as RunConfig;
	}
}

export function EffectPointer_ToString(effect: EffectPointer) {
	return [
		effect.soundEffectTag && `SoundTag(${effect.soundEffectTag})`,
		effect.soundFile && `AudioFile(filePath:${effect.soundFile.filePath
			} volume:${effect.soundFile.volume ?? "n/a"
			} durationLimit:${effect.soundFile.durationLimit ?? "n/a"
			} loop:${effect.soundFile.loop ?? "n/a"
			} runCount:${effect.soundFile.runCount ?? "n/a"
			} wordsIgnoreGroup:${effect.soundFile.wordsIgnoreGroup ?? "n/a"})`,
		effect.tts && `TTS(soundTag:"${effect.tts.soundTag ?? "n/a"
			}" volume:${effect.tts.volume ?? "n/a"
			} text:"${effect.tts.text}")`,
		effect.shakeEffectTag && `ShakeTag(${effect.shakeEffectTag})`,
		effect.lightEffectTag && `LightTag(${effect.lightEffectTag})`,
		effect.scriptEffectTag && `ScriptTag(${effect.scriptEffectTag})`,
	].filter(a=>a).join(", ");
}